import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { stringify } from 'qs';
import {
  AppBar,
  Box,
  Button,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  Divider,
  Toolbar,
  Typography,
} from '@material-ui/core';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import { getReservationChangeInfo, cancelReservation } from 'api/reservationChangeApi';
import useStyles from './styles';

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

function AutoLink({ text }: { text: string }) {
  if (!text) return <></>;
  const parts = text.split(URL_REGEX);
  return (
    <>
      {parts.map((part, i) => {
        if (part.match(/^https?:\/\//)) {
          return (
            <a key={String(i)} href={part} target="_blank" rel="noopener noreferrer">
              {part}
            </a>
          );
        }
        return part;
      })}
    </>
  );
}

interface ReservationChangeData {
  reservationInformationId: string;
  orgId: string;
  contractCourseId: string;
  neighborhoodStoresList: { orgId: string; orgName: string }[];
  reservationNumber: string;
  companyName: string;
  orgName: string;
  courseName: string;
  desiredTime: string;
  treatmentTime: number;
  customerLastName: string;
  customerFirstName: string;
  cautionText: string;
  checkItems: string[];
  customerChangeHour: number;
  customerCancelHour: number;
}

const DAY_OF_WEEK_JA = ['日', '月', '火', '水', '木', '金', '土'];

// WEB での受付期間を過ぎた場合（店舗設定の受付時間超過・URL の有効期限切れ）の案内文。
// target には受付を締め切った操作（「変更・キャンセル」「変更」「キャンセル」）が入る。
function buildWebDeadlinePassedMessage(target: string): string {
  return [
    `WEBでの${target}受付期間を過ぎています。`,
    `ご予約時間が迫っているため、WEBからの${target}はできません。`,
    `${target}をご希望の場合は、ご予約店舗まで直接お電話にてご連絡ください。`,
    'お手数をおかけいたしますが、よろしくお願いいたします。',
  ].join('\n');
}

const WEB_DEADLINE_PASSED_MESSAGE = buildWebDeadlinePassedMessage('変更・キャンセル');
const WEB_CHANGE_DEADLINE_PASSED_MESSAGE = buildWebDeadlinePassedMessage('変更');
const WEB_CANCEL_DEADLINE_PASSED_MESSAGE = buildWebDeadlinePassedMessage('キャンセル');

function formatDesiredTime(desiredTime: string, treatmentTime: number): string {
  const start = moment(desiredTime);
  const dow = DAY_OF_WEEK_JA[start.day()];
  return `${start.format('YYYY年MM月DD日')}（${dow}）${start.format('HH:mm')}（日本時間）〜（約${treatmentTime}分）`;
}

const useDialogStyles = makeStyles((theme: Theme) => createStyles({
  dialogTitle: {
    textAlign: 'center',
    fontWeight: 700,
    fontSize: '1.1rem',
    padding: '20px 24px 12px',
  },
  dialogMessage: {
    textAlign: 'center',
    fontSize: '0.95rem',
    color: '#333',
    padding: '4px 24px 20px',
  },
  dialogActions: {
    display: 'flex',
    borderTop: '1px solid #e0e0e0',
  },
  dialogBtn: {
    flex: 1,
    padding: '14px 0',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
    '&:first-child': { borderRight: '1px solid #e0e0e0' },
    '&:hover': { backgroundColor: '#fafafa' },
  },
}));

function ReservationChange() {
  const classes = useStyles();
  const dialogClasses = useDialogStyles();
  const location = useLocation();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const urlParams = new URLSearchParams(location.search);
  const token = urlParams.get('token') || '';
  const companyCode = urlParams.get('companyCode') || '';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ReservationChangeData | null>(null);
  const [alreadyCancelled, setAlreadyCancelled] = useState(false);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const { isChangeDisabled, isCancelDisabled } = useMemo(() => {
    if (!data?.desiredTime) {
      return { isChangeDisabled: false, isCancelDisabled: false };
    }
    const hoursUntilDesired = (moment(data.desiredTime).valueOf() - Date.now()) / 3600000;
    return {
      isChangeDisabled: !!data.customerChangeHour && hoursUntilDesired < data.customerChangeHour,
      isCancelDisabled: !!data.customerCancelHour && hoursUntilDesired < data.customerCancelHour,
    };
  }, [data]);

  // 予約詳細の下部に出す受付期間超過の案内文（締め切った操作に応じて文言を切り替える）
  let deadlineMessage = '';
  if (isChangeDisabled && isCancelDisabled) {
    deadlineMessage = WEB_DEADLINE_PASSED_MESSAGE;
  } else if (isChangeDisabled) {
    deadlineMessage = WEB_CHANGE_DEADLINE_PASSED_MESSAGE;
  } else if (isCancelDisabled) {
    deadlineMessage = WEB_CANCEL_DEADLINE_PASSED_MESSAGE;
  }

  useEffect(() => {
    if (!token || !companyCode) {
      setLoading(false);
      return;
    }
    getReservationChangeInfo(token, companyCode)
      .then((res) => {
        setData(res.data || null);
      })
      .catch((error: any) => {
        const errorCode = error?.response?.data?.errors?.[0]?.defaultMessage;
        if (errorCode === 'RESERVATION_ALREADY_CANCELLED') {
          setAlreadyCancelled(true);
        }
        if (errorCode === 'TOKEN_EXPIRED') {
          setTokenExpired(true);
        }
        setData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token, companyCode]);

  const handleReselect = () => {
    history.push(`/contact/reservationMenuReselect?${stringify({ token, companyCode })}`);
  };

  const handleCancelClick = () => {
    if (isCancelDisabled) return;
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = () => {
    setCancelling(true);
    cancelReservation(token, companyCode)
      .then(() => {
        setCancelDialogOpen(false);
        window.location.assign(`/contact/reservationChangeComplete?${stringify({ token, companyCode, type: 'cancel' })}`);
      })
      .catch(() => {
        enqueueSnackbar('キャンセルに失敗しました。時間をおいて再度お試しください。', { variant: 'error' });
        setCancelDialogOpen(false);
      })
      .finally(() => {
        setCancelling(false);
      });
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" mt={6}>
          <CircularProgress color="primary" />
        </Box>
      );
    }

    if (alreadyCancelled) {
      return (
        <Box className={classes.invalidTokenBox}>
          <Typography variant="h6" gutterBottom={true}>
            予約はキャンセル済みです
          </Typography>
          <Typography variant="body2">
            この予約はすでにキャンセルされています。またのご利用をお待ちしております。
          </Typography>
        </Box>
      );
    }

    if (tokenExpired) {
      return (
        <Box className={classes.deadlinePassedBox}>
          <Typography variant="body2" className={classes.deadlineMessageText}>
            {WEB_DEADLINE_PASSED_MESSAGE}
          </Typography>
        </Box>
      );
    }

    if (!token || !companyCode || !data) {
      return (
        <Box className={classes.invalidTokenBox}>
          <Typography variant="h6" gutterBottom={true}>
            リンクが無効です
          </Typography>
          <Typography variant="body2">
            このURLは無効または有効期限切れです。
          </Typography>
        </Box>
      );
    }

    return (
      <>
        <Typography className={classes.pageTitle}>
          予約詳細
        </Typography>

        <Box className={classes.salonInfoBox}>
          <Typography className={classes.salonName}>
            {data.companyName}
          </Typography>
          <Typography className={classes.branchName}>
            {data.orgName}
          </Typography>
          <Typography className={classes.courseName}>
            {data.courseName}
          </Typography>
        </Box>

        <Box className={classes.dateTimeRow}>
          <Typography component="span" className={classes.dateTimeLabel}>
            予約日時
          </Typography>
          <Typography component="span" variant="body2">
            {formatDesiredTime(data.desiredTime, data.treatmentTime)}
          </Typography>
        </Box>

        <Divider className={classes.divider} />

        <Typography className={classes.sectionTitle}>予約者</Typography>
        <Box className={classes.reserverBox}>
          <Box className={classes.reserverRow}>
            <Typography component="span" className={classes.reserverLabel}>
              予約番号：
            </Typography>
            <Typography component="span" className={classes.reserverValue}>
              {data.reservationNumber}
            </Typography>
          </Box>
          <Box className={classes.reserverRow}>
            <Typography component="span" className={classes.reserverLabel}>
              氏名　　：
            </Typography>
            <Typography component="span" className={classes.reserverValue}>
              {`${data.customerLastName} ${data.customerFirstName}`}
            </Typography>
          </Box>
        </Box>

        <Typography className={classes.sectionTitle}>注意事項</Typography>
        <Box className={classes.cautionBox}>
          <AutoLink text={data.cautionText} />
        </Box>

        <Typography className={classes.sectionTitle}>必ずご確認ください</Typography>
        <Box className={classes.checkItemsBox}>
          {(data.checkItems || []).map((item) => (
            <Typography key={item} style={{ fontSize: '0.8rem', lineHeight: 1.7 }}>
              <AutoLink text={item} />
            </Typography>
          ))}
        </Box>

        <Box className={classes.changeSection}>
          <Typography className={classes.changeSectionTitle}>
            予約内容の変更
          </Typography>
          <Button
            variant="contained"
            color="primary"
            fullWidth={true}
            className={classes.actionButton}
            disabled={isChangeDisabled}
            onClick={() => history.push(
              `/contact/reservationChangeDateChange?${stringify({
                token,
                companyCode,
                orgName: data.orgName,
                orgId: data.orgId,
              })}`,
              { neighborhoodStoresList: data.neighborhoodStoresList || [] },
            )}
          >
            日時を変更
          </Button>
          <Button
            variant="contained"
            color="primary"
            fullWidth={true}
            className={classes.actionButton}
            disabled={isChangeDisabled}
            onClick={handleReselect}
          >
            メニューから再選択
          </Button>
          <Typography
            component="span"
            className={classes.cancelLink}
            style={isCancelDisabled ? { pointerEvents: 'none', opacity: 0.5 } : undefined}
            onClick={handleCancelClick}
          >
            予約内容のキャンセル
          </Typography>
          {(isChangeDisabled || isCancelDisabled) && (
            <Box textAlign="center" mt={2}>
              <Typography
                variant="body2"
                className={classes.deadlineMessageText}
                style={{
                  color: '#d32f2f',
                  fontWeight: 600,
                }}
              >
                {deadlineMessage}
              </Typography>
            </Box>
          )}
        </Box>
      </>
    );
  };

  return (
    <div className={classes.root}>
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="xs" fullWidth={true}>
        <DialogContent style={{ padding: 0 }}>
          <p className={dialogClasses.dialogTitle}>確認</p>
          <p className={dialogClasses.dialogMessage}>予約をキャンセルしますがよろしいですか？</p>
          <div className={dialogClasses.dialogActions}>
            <button type="button" className={dialogClasses.dialogBtn} onClick={() => setCancelDialogOpen(false)} disabled={cancelling}>
              いいえ
            </button>
            <button type="button" className={dialogClasses.dialogBtn} onClick={handleCancelConfirm} disabled={cancelling}>
              はい
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <AppBar position="static" className={classes.appBar}>
        <Toolbar>
          <Typography variant="h6" className={classes.appBarTitle}>
            {data ? data.orgName : ''}
          </Typography>
        </Toolbar>
      </AppBar>
      <Container maxWidth="sm" className={classes.container}>
        {renderContent()}
      </Container>
    </div>
  );
}

export default ReservationChange;
